import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextareaAutosize, List, ListItem, ListItemText, IconButton, Checkbox } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

const ProjectManagerComponent = () => {
    const { user } = useUser();
    const [projects, setProjects] = useState([]);
    const [currentProject, setCurrentProject] = useState({ id: null, name: '', extra: '{}' });
    const [extraJSON, setExtraJSON] = useState('{}');
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [users, setUsers] = useState([]);
    const [linkedUsers, setLinkedUsers] = useState([]);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/projects`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setProjects(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching projects');
            }
        };

        const fetchUsers = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_AUTH_URL}/users`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setUsers(response.data);
            } catch (err) {
                setError(err.response?.data?.message || 'An error occurred while fetching users');
            }
        };

        if (user.token) {
            fetchProjects();
            if (user.hasRoles(['jedimaster'])){
                fetchUsers();
            }
        }
    }, [user]);

    useEffect(() => {
        const fetchLinkedUsers = async () => {
            if (currentProject.id) {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/user_projects/project/${currentProject.id}`, {
                        headers: { Authorization: `Bearer ${user.token}` },
                    });
                    setLinkedUsers(response.data.map(user => user.id));
                } catch (err) {
                    setError(err.response?.data?.message || 'An error occurred while fetching linked users');
                }
            }
        };

        if (open) {
            if (user.hasRoles(['jedimaster'])){
                fetchLinkedUsers();
            }
        }
    }, [open, currentProject.id, user]);

    const handleOpen = (project) => {
        setIsEditMode(!!project);
        setCurrentProject(project || { id: null, name: '', extra: '{}' });
        setExtraJSON(project ? JSON.stringify(project.extra, null, 2) : '{}');
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setCurrentProject({ id: null, name: '', extra: '{}' });
        setExtraJSON('{}');
        setError('');
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setCurrentProject({ ...currentProject, [name]: value });
    };

    const handleExtraChange = (event) => {
        const { value } = event.target;
        setExtraJSON(value);
    };

    const handleAddOrUpdateProject = async () => {
        try {
            const parsedExtra = JSON.parse(extraJSON);
            const updatedProject = { ...currentProject, extra: parsedExtra };

            if (isEditMode) {
                const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/projects/${currentProject.id}`, updatedProject, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setProjects(projects.map((project) => (project.id === currentProject.id ? response.data : project)));
            } else {
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/projects`, updatedProject, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setProjects([...projects, response.data]);
            }
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while saving the project');
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/projects/${id}`, {
                headers: { Authorization: `Bearer ${user.token}` },
            });
            setProjects(projects.filter(project => project.id !== id));
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while deleting the project');
        }
    };

    const handleUserCheckboxChange = async (userId) => {
        try {
            if (linkedUsers.includes(userId)) {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/user_projects/${userId}/${currentProject.id}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setLinkedUsers(linkedUsers.filter(id => id !== userId));
            } else {
                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/user_projects/${userId}/${currentProject.id}`, {}, {
                    headers: { Authorization: `Bearer ${user.token}` },
                });
                setLinkedUsers([...linkedUsers, userId]);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while updating user links');
        }
    };

    if (!user.token) {
        return <p>You must be logged in to manage projects.</p>;
    }

    return (
        <div>
            {user.hasRoles(['jedimaster']) && (
                <Button variant="contained" color="primary" onClick={() => handleOpen(null)}>
                    Add Project
                </Button>
            )}
            {error && typeof error === 'string' && <p>{error}</p>}
            <List>
                {Array.isArray(projects) && projects.map(project => (
                    <ListItem key={project.id}>
                        {user.hasAnyRole(['jedimaster', 'admin']) && (
                            <IconButton edge="end" aria-label="edit" onClick={() => handleOpen(project)} sx={{ mr: 1 }} >
                                <EditIcon />
                            </IconButton>
                        )}
                        <ListItemText primary={project.name} secondary={JSON.stringify(project.extra)} />
                        {user.hasRoles(['jedimaster']) && (
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(project.id)}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </ListItem>
                ))}
            </List>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{isEditMode ? 'Edit Project' : 'Add New Project'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Name"
                        type="text"
                        fullWidth
                        name="name"
                        value={currentProject.name}
                        onChange={handleInputChange}
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        id="description"
                        label="Description"
                        type="text"
                        fullWidth
                        name="description"
                        value={currentProject.description}
                        onChange={handleInputChange}
                    />
                    <TextareaAutosize
                        aria-label="extra"
                        minRows={5}
                        placeholder="Extra JSON"
                        name="extra"
                        value={extraJSON}
                        onChange={handleExtraChange}
                        style={{ width: '100%', marginTop: '16px', padding: '8px' }}
                    />
                    <List>
                        {users.map(user => (
                            <ListItem key={user.id}>
                                <ListItemText primary={user.name} />
                                <Checkbox
                                    checked={linkedUsers.includes(user.id)}
                                    onChange={() => handleUserCheckboxChange(user.id)}
                                />
                            </ListItem>
                        ))}
                    </List>
                    {error && <p>{error}</p>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleAddOrUpdateProject}>{isEditMode ? 'Update Project' : 'Add Project'}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ProjectManagerComponent;
